/**
 * "Entering for <day>" with previous/next day and a date picker (#366), for the phone entry
 * screens. A past day is allowed (back-filling DOC 1-9 at DOC 10) and is marked late afterwards;
 * the future and days before the crop started are not offered (the API refuses them too).
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { addPhDays, displayPhDate } from '@/lib/phDate';
import { cn } from '@/lib/cn';

interface Props {
  date: string;
  today: string;
  /** Earliest selectable day (the crop's start), if known. */
  minDate?: string | null;
  onChange: (date: string) => void;
  className?: string;
  disabled?: boolean;
}

export function DateBar({ date, today, minDate, onChange, className, disabled }: Props) {
  // A typed date outside the allowed range is clamped, never navigated to.
  const pick = (d: string) => onChange(d > today ? today : minDate && d < minDate ? minDate : d);
  const past = date < today;
  const canPrev = !minDate || date > minDate;
  const canNext = date < today;
  const step = 'flex min-h-11 min-w-11 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-900 touch-manipulation disabled:opacity-40';
  return (
    <div className={cn('space-y-1', className)} data-date-bar>
      <div className="flex items-center gap-2">
        <button type="button" aria-label="Previous day" disabled={disabled || !canPrev} onClick={() => pick(addPhDays(date, -1))} className={step}>
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <label className="relative flex min-h-11 flex-1 items-center justify-center rounded-lg border border-gray-300 bg-white px-3 text-[15px] font-semibold text-gray-900">
          <span data-date-label>
            {past ? 'Entering for ' : ''}
            {displayPhDate(date, { weekday: 'short', month: 'short', day: 'numeric' })}
            {!past && ' · Today'}
          </span>
          <input
            type="date"
            aria-label="Pick a day"
            value={date}
            min={minDate ?? undefined}
            max={today}
            disabled={disabled}
            onChange={(e) => e.target.value && pick(e.target.value)}
            className="absolute -inset-px h-[calc(100%+2px)] w-[calc(100%+2px)] cursor-pointer opacity-0"
          />
        </label>
        <button type="button" aria-label="Next day" disabled={disabled || !canNext} onClick={() => pick(addPhDays(date, 1))} className={step}>
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      {past && (
        <p className="flex items-center justify-between gap-2 text-xs text-sky-800" data-past-day-note>
          <span>Past day: what you enter is marked Late.</span>
          <button type="button" onClick={() => onChange(today)} disabled={disabled} className="min-h-11 px-2 font-semibold text-primary-600">
            Back to today
          </button>
        </p>
      )}
    </div>
  );
}
